import {
  RateScheduleInlineUpdate,
  RateScheduleSelection,
  RateScheduleUpdateTransaction,
  UpdateRatePayload,
} from 'appRedux/reducers/chargeSchedule/chargeType';

type UpdateContext = Pick<UpdateRatePayload, 'booking_id' | 'register_id'>;
type RateValueField = Exclude<keyof UpdateRatePayload, keyof UpdateContext | 'items'>;
type RateValueInput = Partial<Record<RateValueField, unknown>>;

type BuildRateUpdatePayloadsOptions = {
  context: UpdateContext;
  inlineUpdates?: RateScheduleInlineUpdate[];
  transactions?: RateScheduleUpdateTransaction[];
  valueUpdate?: RateValueInput;
};

const rateValueFields: RateValueField[] = [
  'room_rate',
  'extra_adult',
  'extra_child',
  'extra_infant',
  'mealtype_id',
  'rateplan_id',
];

const normalizeRateValues = (valueUpdate: RateValueInput): Partial<UpdateRatePayload> => {
  const values: Partial<UpdateRatePayload> = {};

  rateValueFields.forEach((field) => {
    const value = valueUpdate[field];
    if (value === undefined || value === '') return;
    if (value === null) {
      values[field] = null;
      return;
    }

    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) values[field] = numericValue;
  });

  return values;
};

export const buildInlineRateUpdatePayloads = (
  updates: RateScheduleInlineUpdate[],
  context: UpdateContext
): UpdateRatePayload[] => {
  const batches = new Map<
    string,
    {
      items: Map<string, RateScheduleSelection>;
      values: RateScheduleInlineUpdate['values'];
    }
  >();

  updates.forEach((update) => {
    const sortedValues = Object.entries(update.values).sort(([left], [right]) =>
      left.localeCompare(right)
    );
    const valuesKey = JSON.stringify(sortedValues);
    const batch = batches.get(valuesKey) || {
      items: new Map<string, RateScheduleSelection>(),
      values: update.values,
    };
    const itemKey = `${update.source}:${update.id}`;
    const currentItem = batch.items.get(itemKey);

    batch.items.set(itemKey, {
      id: update.id,
      source: update.source,
      dates: Array.from(new Set([...(currentItem?.dates || []), update.date])),
    });
    batches.set(valuesKey, batch);
  });

  return Array.from(batches.values()).map((batch) => ({
    ...context,
    items: Array.from(batch.items.values()),
    ...batch.values,
  }));
};

export const buildRateUpdatePayloads = ({
  context,
  inlineUpdates = [],
  transactions = [],
  valueUpdate = {},
}: BuildRateUpdatePayloadsOptions): UpdateRatePayload[] => {
  if (inlineUpdates.length > 0) {
    return buildInlineRateUpdatePayloads(inlineUpdates, context);
  }

  return [
    {
      ...context,
      items: transactions.map((transaction) => ({
        id: Number(transaction.id),
        ...(transaction.source ? { source: transaction.source } : {}),
        dates: transaction.selectedRows.map((row) => row.date),
      })),
      ...normalizeRateValues(valueUpdate),
    },
  ];
};
